
import React, { useState } from 'react';
import './HomePage.css';
import { useApp } from '../../context/AppContext';
import PostCard from '../../components/PostCard/PostCard';
import StoryViewer from '../../components/StoryViewer/StoryViewer';
import AdminContentDashboard from '../Admin/AdminContentDashboard';
import SubscribeModal, { SubscribeModalTier } from '../../components/SubscribeModal/SubscribeModal';

const HomePage: React.FC = () => {
  const { posts, adminUser, currentUser, subscribe, subscriptionTiers, isAdminView } = useApp();
  const [pendingTier, setPendingTier] = useState<SubscribeModalTier | null>(null);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const regularPosts = posts.filter(p => !p.isStory).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const DAY_MS = 24 * 60 * 60 * 1000;
  const newPostsCount = regularPosts.filter(p => Date.now() - p.timestamp.getTime() < DAY_MS).length;
  // Story queue (rolling 24h) — used to gate the story-ring around the
  // banner avatar so we don't waste vertical space on a separate ribbon.
  const stories = posts.filter(p =>
    p.isStory
    && (!p.expiresAt || p.expiresAt > new Date())
    && Date.now() - p.timestamp.getTime() < DAY_MS
  );
  const hasStories = stories.length > 0;

  if (isAdminView) {
    return <AdminContentDashboard />;
  }

  if (!currentUser.isSubscribed && !isAdminView) {
    const hasRealStats = adminUser.subscribers > 0 || adminUser.posts > 0 || adminUser.likes > 0;
    return (
      <div className="subscribe-gate">
        <div className="gate-hero">
          {adminUser.coverImage && (
            <img
              src={adminUser.coverImage}
              alt=""
              className="gate-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div className="gate-cover-overlay" />
          <div className="gate-avatar-wrap">
            <img src={adminUser.avatar} alt={adminUser.displayName} className="gate-avatar" />
            {adminUser.online && <span className="gate-online-dot" title="Online now" />}
          </div>
        </div>

        <div className="gate-identity">
          <h1>
            {adminUser.displayName}
            <svg className="verified-badge" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2l2.39 1.74 2.94-.18.46 2.91 2.61 1.39-1.26 2.66 1.26 2.66-2.61 1.39-.46 2.91-2.94-.18L12 22l-2.39-1.74-2.94.18-.46-2.91-2.61-1.39L4.86 13.5 3.6 10.84l2.61-1.39.46-2.91 2.94.18L12 2z" fill="url(#vGrad)"/>
              <defs>
                <linearGradient id="vGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6"/>
                  <stop offset="100%" stopColor="#ec4899"/>
                </linearGradient>
              </defs>
              <path d="M9 12.5l2 2 4-4.5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </h1>
          <p className="gate-username">@{adminUser.username}</p>
          <p className="gate-bio">{adminUser.bio}</p>

          {hasRealStats && (
            <div className="gate-stats">
              {adminUser.subscribers > 0 && (
                <div className="gate-stat">
                  <span className="stat-value">{adminUser.subscribers >= 1000 ? `${(adminUser.subscribers / 1000).toFixed(1)}K` : adminUser.subscribers}</span>
                  <span className="stat-label">Fans</span>
                </div>
              )}
              {adminUser.posts > 0 && (
                <div className="gate-stat">
                  <span className="stat-value">{adminUser.posts}</span>
                  <span className="stat-label">Posts</span>
                </div>
              )}
              {adminUser.likes > 0 && (
                <div className="gate-stat">
                  <span className="stat-value">{adminUser.likes >= 1000000 ? `${(adminUser.likes / 1000000).toFixed(1)}M` : adminUser.likes >= 1000 ? `${(adminUser.likes / 1000).toFixed(1)}K` : adminUser.likes}</span>
                  <span className="stat-label">Likes</span>
                </div>
              )}
            </div>
          )}

          {adminUser.online && (
            <p className="gate-online-pill">
              <span className="dot" /> Active now
            </p>
          )}
        </div>

        <div className="subscription-tiers">
          <h2>Unlock everything</h2>
          <p className="subscription-subtitle">One subscription. Every post, story, and DM access — no extra charges.</p>
          {subscriptionTiers.map(tier => {
            const getPeriodLabel = (id: string) => {
              switch(id) {
                case 'monthly': return '/month';
                case 'quarterly': return '/3 months';
                case 'yearly': return '/year';
                case 'lifetime': return ' one-time';
                default: return '';
              }
            };
            return (
              <div key={tier.id} className={`tier-card ${tier.isPopular ? 'popular' : ''} ${tier.id === 'lifetime' ? 'lifetime' : ''}`}>
                {tier.isPopular && <span className="popular-badge">MOST POPULAR</span>}
                {tier.id === 'lifetime' && <span className="popular-badge best-value">BEST VALUE</span>}
                <div className="tier-header">
                  <h3>{tier.name}</h3>
                  <div className="tier-price">
                    <span className="price">${tier.price}</span>
                    <span className="period">{getPeriodLabel(tier.id)}</span>
                  </div>
                </div>
                <ul className="tier-features">
                  {tier.features.map((feature, i) => (
                    <li key={i}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="subscribe-btn" onClick={() => setPendingTier(tier as SubscribeModalTier)}>
                  Subscribe Now
                </button>
              </div>
            );
          })}
          <p className="subscription-fineprint">
            Secured by PayPal · Cancel anytime · Instant access after payment
          </p>
        </div>
        {pendingTier && (
          <SubscribeModal
            tier={pendingTier}
            user={{ id: currentUser.id, email: currentUser.email, displayName: currentUser.displayName }}
            onClose={() => setPendingTier(null)}
            onSuccess={() => { subscribe(pendingTier.id); setPendingTier(null); }}
          />
        )}
      </div>
    );
  }

  // Subscribed fan view
  return (
    <div className="home-page">
      {/* The story ring + viewer are now part of the creator banner —
          the separate Stories ribbon was duplicate vertical chrome for
          a single-creator product. Tapping the ringed avatar opens the
          full story sequence. */}
      <div className="creator-banner">
        {adminUser.coverImage && (
          <img
            src={adminUser.coverImage}
            alt=""
            className="banner-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div className="banner-overlay" />
        <div className="banner-content">
          <button
            type="button"
            className={`banner-avatar-wrap${hasStories ? ' has-story' : ''}`}
            onClick={() => { if (hasStories) setShowStoryViewer(true); }}
            disabled={!hasStories}
            aria-label={hasStories ? `View ${stories.length} new ${stories.length === 1 ? 'story' : 'stories'} from ${adminUser.displayName}` : `${adminUser.displayName} avatar`}
          >
            <img src={adminUser.avatar} alt={adminUser.displayName} className="banner-avatar" />
            {adminUser.online && <span className="banner-online-dot" title="Online now" />}
            {hasStories && stories.length > 1 && (
              <span className="banner-story-count" aria-hidden="true">{stories.length}</span>
            )}
          </button>
          <div className="banner-info">
            <h2>
              {adminUser.displayName}
              <svg className="verified" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              {newPostsCount > 0 && <span className="new-badge">{newPostsCount} NEW</span>}
            </h2>
            <p>
              {adminUser.online ? 'Online · Replies fast' : 'Offline'}
              {adminUser.location && ` · ${adminUser.location}`}
              {hasStories && (
                <>
                  {' · '}
                  <span className="banner-story-hint">Tap photo · {stories.length} {stories.length === 1 ? 'story' : 'stories'}</span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
      {showStoryViewer && hasStories && (
        <StoryViewer
          stories={stories}
          initialIndex={0}
          onClose={() => setShowStoryViewer(false)}
        />
      )}

      <div className="feed">
        {regularPosts.length === 0 ? (
          <div className="feed-empty">
            <span className="feed-empty-icon">✨</span>
            <h3>Fresh drops landing soon</h3>
            <p>Jalen posts on a rolling schedule — your feed updates the moment new content goes live.</p>
          </div>
        ) : (
          regularPosts.map(post => (
            <PostCard key={post.id} post={post} />
          ))
        )}
      </div>
    </div>
  );
};

export default HomePage;
